if (!window.__productImageDrawerInitialized) {
  window.__productImageDrawerInitialized = true;

  /* ============================================================
   * SECTION MAP — search for the § symbol + tag to jump to each section
   *
   * §DEBUG_FLAGS         — Feature flags read from URL params / localStorage
   * §HARD_LOCK           — Body-position scroll lock (apply / release)
   * §DEBUG_STORAGE       — localStorage keys & ring-buffer read/write helpers
   * §DEBUG_API           — window.pidDebug* / window.pidOmniDump public API
   * §LOG                 — __pidLog, __pidNow, __pidShortEl, snapshot helpers
   * §GESTURE_DEBUG       — __pidAttachGestureDebug: per-element touch tracing
   *
   * §CLASS               — class ProductImageDrawer { constructor … }
   *   §CONSTRUCTOR         — constructor: DOM refs, drag state, zoom UI blockers, click handlers
   *   §REINIT              — reinit(): teardown + re-bind after DOM replacement
   *   §ZOOM_CORE           — readActualZoomScale / getZoomScale / zoomToScale
   *   §ZOOM_STEPS          — zoomStepIn / zoomStepOut / snapToStep / updateZoomReadout
   *   §ZOOM_EVENTS         — zoomInAtCenter / getSafeZoomEvent / toggleZoomAtEvent / onZoomControlPointerDown
   *   §VIDEO_SHIELDS       — iframe shield enable/disable/reenable/reset helpers
   *   §PANEL               — hasPanel / setActivePanel / goToPanel / wrapForward / wrapBackward
   *   §NAV                 — bindMainNav / bindGridKeyboardNav / syncThumbsToMain / syncMainCarouselToActiveSlide
   *   §VARIANT             — updateVariantDisplay / selectVariantOnPage
   *   §SWIPER_INIT         — initSwipers(): thumbs + main Swiper construction, wheel/click/dblclick zoom
   *   §SCROLL_LOCK         — lockScroll / unlockScroll
   *   §OPEN_CLOSE          — open() / close() / onBackdropClick / onKeyDown / isZoomed
   *   §DRAG                — onPointerDown / onPointerMove / onPointerUp (dismiss / pan / swipe-up)
   *   §TOUCH_ZOOM          — _attachTouchZoom(): pinch-zoom + double-tap for touch devices
   *
   * §OPEN_HANDLER        — document click handler for [data-open-product-image-drawer]
   * §INIT                — DOMContentLoaded: instantiate per-root, window.openProductImageDrawer
   * ============================================================ */

  // §DEBUG_FLAGS
  /*
    To enter debug mode,set pid_debug=1 in localStorage.
  */
  const __pidDebugEnabled = (() => {
    try {
      if (window.__pidDebugPID) return true;
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("pid_debug") === "1") return true;
      if (qs.get("pid_debug") === "true") return true;
      if (localStorage.getItem("pid_debug") === "1") return true;
      if (localStorage.getItem("pid_debug") === "true") return true;
    } catch (_) { }
    return false;
  })();

  const __pidDebugBlockNativeHandoff = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("pid_block_handoff") === "1") return true;
      if (localStorage.getItem("pid_block_handoff") === "1") return true;
      if (localStorage.getItem("pid_block_handoff") === "true") return true;
    } catch (_) { }
    return false;
  })();

  const __pidDebugHardScrollLock = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("pid_hard_lock") === "1") return true;
      if (localStorage.getItem("pid_hard_lock") === "1") return true;
      if (localStorage.getItem("pid_hard_lock") === "true") return true;
    } catch (_) { }
    return false;
  })();

  const __pidDebugDisableZoom = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("pid_disable_zoom") === "1") return true;
      if (qs.get("pid_disable_zoom") === "true") return true;
      if (localStorage.getItem("pid_disable_zoom") === "1") return true;
      if (localStorage.getItem("pid_disable_zoom") === "true") return true;
    } catch (_) { }
    return false;
  })();

  // §HARD_LOCK
  const __pidHardLock = {
    active: false,
    scrollY: 0,
    apply() {
      if (this.active) return;
      this.active = true;
      try {
        this.scrollY = window.scrollY || window.pageYOffset || 0;
      } catch (_) {
        this.scrollY = 0;
      }
      try {
        const body = document.body;
        if (!body) return;
        body.dataset.pidHardLock = "1";
        body.style.position = "fixed";
        body.style.top = `-${this.scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden";
      } catch (_) { }
      __pidLog("hard scroll lock applied", { scrollY: this.scrollY });
    },
    release() {
      if (!this.active) return;
      this.active = false;
      const y = this.scrollY || 0;
      try {
        const body = document.body;
        if (body) {
          delete body.dataset.pidHardLock;
          body.style.position = "";
          body.style.top = "";
          body.style.left = "";
          body.style.right = "";
          body.style.width = "";
          body.style.overflow = "";
        }
      } catch (_) { }
      try {
        window.scrollTo(0, y);
      } catch (_) { }
      __pidLog("hard scroll lock released", { scrollY: y });
    },
  };

  // §DEBUG_STORAGE
  const __PID_DEBUG_STORAGE_KEY = "pid_debug_log_v1";
  const __PID_DEBUG_META_KEY = "pid_debug_meta_v1";
  const __PID_DEBUG_RUNTIME_KEY = "pid_debug_runtime_v1";
  const __PID_DEBUG_MAX_EVENTS = 300; // keep small to avoid localStorage bloat

  const __pidNow = () => {
    try {
      // relative timestamp is usually more useful for gesture analysis
      return {
        t: typeof performance?.now === "function" ? Math.round(performance.now()) : null,
        unix: Date.now(),
      };
    } catch (_) {
      return { t: null, unix: Date.now() };
    }
  };

  const __pidReadStore = () => {
    try {
      const raw = localStorage.getItem(__PID_DEBUG_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const __pidWriteStore = (events) => {
    try {
      localStorage.setItem(__PID_DEBUG_STORAGE_KEY, JSON.stringify(events));
    } catch (_) { }
  };

  const __pidAppendStore = (entry) => {
    try {
      const events = __pidReadStore();
      events.push(entry);
      if (events.length > __PID_DEBUG_MAX_EVENTS) {
        events.splice(0, events.length - __PID_DEBUG_MAX_EVENTS);
      }
      __pidWriteStore(events);
    } catch (_) { }
  };

  // §DEBUG_API
  // Expose helpers so you can pull logs after reload.
  // Intentionally namespaced off window to make iPad debugging easier.
  const __pidExposeDebugApi = () => {
    if (!__pidDebugEnabled) return;
    if (window.pidDebugDump && window.pidDebugClear) return;

    const safeJson = (v) => {
      try {
        return JSON.stringify(v);
      } catch (_) {
        try {
          return String(v);
        } catch (_) {
          return "[unstringifiable]";
        }
      }
    };

    window.pidDebugFlags = () => {
      return {
        pid_debug: true,
        pid_block_handoff: __pidDebugBlockNativeHandoff,
        pid_hard_lock: __pidDebugHardScrollLock,
        pid_disable_zoom: __pidDebugDisableZoom,
        href: window.location.href,
        ua: navigator.userAgent,
      };
    };

    window.pidDebugRuntime = () => {
      try {
        const raw = localStorage.getItem(__PID_DEBUG_RUNTIME_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        return null;
      }
    };

    // One-call "give me everything useful" dump (touch friendly).
    window.pidOmniDump = ({
      tailBeforeUnload = 120,
      tailWindowScroll = 60,
      tailMedia = 80,
    } = {}) => {
      const lines = [];
      const events = __pidReadStore();
      const runtime = window.pidDebugRuntime?.() || null;

      const count = (needle) => {
        let c = 0;
        for (const e of events) {
          try {
            if (safeJson(e)?.includes(needle)) c++;
          } catch (_) { }
        }
        return c;
      };

      const lastIndexOf = (needle) => {
        for (let i = events.length - 1; i >= 0; i--) {
          try {
            if (safeJson(events[i])?.includes(needle)) return i;
          } catch (_) { }
        }
        return -1;
      };

      const lastNav = (() => {
        try {
          return __pidGetNavType();
        } catch (_) {
          return null;
        }
      })();

      const flags = window.pidDebugFlags?.() || {};
      const hardLockApplied =
        (document.body && document.body.dataset && document.body.dataset.pidHardLock === "1") ||
        false;

      // Scroll stats from logged window scroll events
      let scrollMin = null;
      let scrollMax = null;
      let scrollLast = null;
      for (const e of events) {
        try {
          const args = e?.args || [];
          if (args?.[0] !== "window scroll") continue;
          const payload = args?.[1];
          const y = payload?.y;
          if (!Number.isFinite(y)) continue;
          scrollLast = y;
          scrollMin = scrollMin === null ? y : Math.min(scrollMin, y);
          scrollMax = scrollMax === null ? y : Math.max(scrollMax, y);
        } catch (_) { }
      }

      const unloadIdx = lastIndexOf("unload fired");
      const pagehideIdx = lastIndexOf("pagehide");
      const endIdx = unloadIdx >= 0 ? unloadIdx : pagehideIdx;

      const n1 = Number.isFinite(parseInt(tailBeforeUnload, 10))
        ? Math.max(20, parseInt(tailBeforeUnload, 10))
        : 120;
      const nScroll = Number.isFinite(parseInt(tailWindowScroll, 10))
        ? Math.max(10, parseInt(tailWindowScroll, 10))
        : 60;
      const nMedia = Number.isFinite(parseInt(tailMedia, 10))
        ? Math.max(20, parseInt(tailMedia, 10))
        : 80;

      const tailSlice = (idx, n) => {
        if (idx < 0) return [];
        const start = Math.max(0, idx - n);
        return events.slice(start, idx + 1).map((e, i) => formatEventLine(e, start + i));
      };

      const filterTail = (needle, n) => {
        const out = [];
        for (let i = events.length - 1; i >= 0 && out.length < n; i--) {
          try {
            const s = safeJson(events[i]);
            if (!s.includes(needle)) continue;
            out.push(formatEventLine(events[i], i));
          } catch (_) { }
        }
        return out.reverse();
      };

      lines.push("== PID OMNI DUMP ==");
      lines.push(`time: ${new Date().toISOString()}`);
      lines.push(`navType(now): ${lastNav || "unknown"}`);
      lines.push(`hardLockApplied(now): ${hardLockApplied ? "yes" : "no"}`);
      lines.push(`flags: ${safeJson(flags)}`);
      lines.push("");

      if (runtime) {
        lines.push("== runtime (persists across reloads) ==");
        lines.push(`startedAtUnix: ${runtime.startedAtUnix || "?"}`);
        lines.push(`lastHref: ${runtime.lastHref || "?"}`);
        lines.push(`lastVisibility: ${runtime.lastVisibility || "?"}`);
        lines.push(`lastInteractionUnix: ${runtime.lastInteractionUnix || "?"}`);
        lines.push(`lastInteraction: ${safeJson(runtime.lastInteraction)}`);
        lines.push(`noise: ${safeJson(runtime.noise)}`);
        lines.push("");
      }

      lines.push("== counts (in stored log buffer) ==");
      lines.push(`eventsTotal: ${events.length}`);
      lines.push(`blockedNativeHandoff: ${count("blocked native handoff (preventDefault)")}`);
      lines.push(`hardLockAppliedLogs: ${count("hard scroll lock applied")}`);
      lines.push(`hardLockReleasedLogs: ${count("hard scroll lock released")}`);
      lines.push(`unloadFired: ${count("unload fired")}`);
      lines.push(`pagehide: ${count("pagehide")}`);
      lines.push(`visibilityHidden: ${count("visibilitychange | hidden")}`);
      lines.push(`windowScrollEvents: ${count("window scroll")}`);
      lines.push(`windowScroll(min/max/last): ${safeJson({ min: scrollMin, max: scrollMax, last: scrollLast })}`);
      lines.push("");

      lines.push("== recent window scroll lines ==");
      lines.push(...filterTail("window scroll", nScroll));
      lines.push("");

      lines.push("== recent mediaList#0 lines ==");
      lines.push(...filterTail("mediaList#0", nMedia));
      lines.push("");

      lines.push("== tail before last unload/pagehide ==");
      if (endIdx >= 0) {
        lines.push(...tailSlice(endIdx, n1));
      } else {
        lines.push("(no unload/pagehide found in buffer)");
      }

      return lines.join("\n");
    };

    // Helpers for touch-only debugging convenience
    window.pidHardLockOn = () => {
      try {
        localStorage.setItem("pid_debug", "1");
        localStorage.setItem("pid_hard_lock", "1");
      } catch (_) { }
      try {
        console.log("[pid-debug] enabled pid_hard_lock; reloading");
      } catch (_) { }
      try {
        location.reload();
      } catch (_) { }
      return true;
    };

    window.pidHardLockOff = () => {
      try {
        localStorage.removeItem("pid_hard_lock");
      } catch (_) { }
      try {
        console.log("[pid-debug] disabled pid_hard_lock; reloading");
      } catch (_) { }
      try {
        location.reload();
      } catch (_) { }
      return true;
    };

    const formatArg = (v) => {
      try {
        if (v === null) return "null";
        if (v === undefined) return "undefined";
        if (typeof v === "string") return v;
        if (typeof v === "number" || typeof v === "boolean") return String(v);
        if (v instanceof Error) return `${v.name}: ${v.message}`;
        return JSON.stringify(v);
      } catch (_) {
        try {
          return String(v);
        } catch (_) {
          return "[unformattable]";
        }
      }
    };

    const formatEventLine = (e, idx) => {
      const t = Number.isFinite(e?.t) ? `${e.t}ms` : "";
      const unix = Number.isFinite(e?.unix) ? new Date(e.unix).toISOString() : "";
      const args = Array.isArray(e?.args) ? e.args : [];
      const head = args.length ? formatArg(args[0]) : "";
      const rest = args.length > 1 ? args.slice(1).map(formatArg).join(" ") : "";
      const parts = [
        String(idx).padStart(3, "0"),
        t,
        unix,
        head,
        rest,
      ].filter(Boolean);
      return parts.join(" | ");
    };

    window.pidDebugDump = ({ filter, limit } = {}) => {
      const events = __pidReadStore();
      const filtered =
        typeof filter === "string" && filter
          ? events.filter((e) => {
            try {
              return JSON.stringify(e)?.includes(filter);
            } catch (_) {
              return false;
            }
          })
          : events;

      const lim = Number.isFinite(parseInt(limit, 10)) ? parseInt(limit, 10) : null;
      const out = lim && lim > 0 ? filtered.slice(Math.max(0, filtered.length - lim)) : filtered;

      const text = out.map((e, i) => formatEventLine(e, i)).join("\n");
      try {
        console.log("[pid-debug] dump(text)", {
          total: events.length,
          returned: out.length,
          filter: filter || null,
        });
      } catch (_) { }
      return text;
    };

    // Raw objects fallback, if needed.
    window.pidDebugDumpJson = ({ filter, limit } = {}) => {
      const events = __pidReadStore();
      const filtered =
        typeof filter === "string" && filter
          ? events.filter((e) => {
            try {
              return JSON.stringify(e)?.includes(filter);
            } catch (_) {
              return false;
            }
          })
          : events;
      const lim = Number.isFinite(parseInt(limit, 10)) ? parseInt(limit, 10) : null;
      return lim && lim > 0 ? filtered.slice(Math.max(0, filtered.length - lim)) : filtered;
    };

    // Quick summary + "tail before unload" in one pasteable string.
    window.pidDebugStats = ({ tailBeforeUnload = 40 } = {}) => {
      const events = __pidReadStore();

      const safeIncludes = (e, needle) => {
        try {
          return JSON.stringify(e)?.includes(needle);
        } catch (_) {
          return false;
        }
      };

      const count = (needle) => {
        let c = 0;
        for (const e of events) if (safeIncludes(e, needle)) c++;
        return c;
      };

      const lastIndexOf = (needle) => {
        for (let i = events.length - 1; i >= 0; i--) {
          if (safeIncludes(events[i], needle)) return i;
        }
        return -1;
      };

      const unloadIdx = lastIndexOf("unload fired");
      const blockedCount = count("blocked native handoff (preventDefault)");
      const windowScrollCount = count("window scroll");

      const lines = [];
      lines.push(`total events: ${events.length}`);
      lines.push(`blocked native handoff: ${blockedCount}`);
      lines.push(`window scroll events: ${windowScrollCount}`);
      lines.push(`unload index: ${unloadIdx >= 0 ? unloadIdx : "none"}`);

      if (unloadIdx >= 0) {
        const nRaw = parseInt(tailBeforeUnload, 10);
        const n = Number.isFinite(nRaw) ? Math.max(10, nRaw) : 40;
        const start = Math.max(0, unloadIdx - n);
        const slice = events.slice(start, unloadIdx + 1);
        lines.push("");
        lines.push(`--- tail before unload (events ${start}..${unloadIdx}) ---`);
        lines.push(slice.map((e, i) => formatEventLine(e, start + i)).join("\n"));
      }

      return lines.join("\n");
    };

    window.pidDebugClear = () => {
      try {
        localStorage.removeItem(__PID_DEBUG_STORAGE_KEY);
        localStorage.removeItem(__PID_DEBUG_META_KEY);
      } catch (_) { }
      try {
        console.log("[pid-debug] cleared localStorage log");
      } catch (_) { }
      return true;
    };

    window.pidDebugLastReload = () => {
      try {
        const raw = localStorage.getItem(__PID_DEBUG_META_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        return null;
      }
    };
  };

  // §LOG
  const __pidLog = (...args) => {
    if (!__pidDebugEnabled) return;
    const stamp = __pidNow();
    const entry = { ...stamp, args };
    __pidAppendStore(entry);
    // keep prefix consistent for filtering in devtools
    try {
      console.log("[pid-debug]", ...args);
    } catch (_) { }
  };

  const __pidShortEl = (el) => {
    if (!el || !el.tagName) return String(el);
    const id = el.id ? `#${el.id}` : "";
    const cls =
      typeof el.className === "string" && el.className.trim()
        ? `.${el.className.trim().split(/\s+/).slice(0, 3).join(".")}`
        : "";
    const data =
      el.getAttribute && el.getAttribute("data-pid-media-list")
        ? "[data-pid-media-list]"
        : el.getAttribute && el.getAttribute("data-product-image-drawer")
          ? "[data-product-image-drawer]"
          : "";
    return `${el.tagName.toLowerCase()}${id}${cls}${data}`;
  };

  const __pidGetNavType = () => {
    try {
      const nav = performance.getEntriesByType?.("navigation")?.[0];
      return nav?.type || null; // 'navigate' | 'reload' | 'back_forward' | 'prerender'
    } catch (_) {
      return null;
    }
  };

  const __pidEventSnapshot = (e) => {
    const base = {
      type: e?.type,
      cancelable: !!e?.cancelable,
      defaultPrevented: !!e?.defaultPrevented,
      timeStamp: typeof e?.timeStamp === "number" ? Math.round(e.timeStamp) : e?.timeStamp,
      target: __pidShortEl(e?.target),
      currentTarget: __pidShortEl(e?.currentTarget),
    };
    try {
      if (e?.touches) {
        const t0 = e.touches[0];
        base.touches = e.touches.length;
        if (t0) base.xy = { x: Math.round(t0.clientX), y: Math.round(t0.clientY) };
      } else if (typeof e?.clientX === "number" && typeof e?.clientY === "number") {
        base.xy = { x: Math.round(e.clientX), y: Math.round(e.clientY) };
      }
      if (e?.pointerType) base.pointerType = e.pointerType;
    } catch (_) { }
    return base;
  };

  // §GESTURE_DEBUG
  const __pidAttachGestureDebug = (root) => {
    if (!__pidDebugEnabled || !root) return;
    if (root.__pidGestureDebugBound) return;
    root.__pidGestureDebugBound = true;

    __pidExposeDebugApi();

    const drawer = root.querySelector("[data-product-image-drawer]");
    const mediaLists = Array.from(root.querySelectorAll("[data-pid-media-list]"));

    __pidLog("debug enabled", {
      ua: navigator.userAgent,
      navType: __pidGetNavType(),
      viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
      root: __pidShortEl(root),
      drawer: __pidShortEl(drawer),
      mediaLists: mediaLists.map(__pidShortEl),
      blockNativeHandoff: __pidDebugBlockNativeHandoff,
      hardScrollLock: __pidDebugHardScrollLock,
      disableZoom: __pidDebugDisableZoom,
    });

    const mkTracker = (label) => {
      let start = null;
      let last = null;
      return {
        onStart: (e) => {
          start = null;
          last = null;
          try {
            const p =
              e?.touches?.[0] ||
              (typeof e?.clientX === "number" ? { clientX: e.clientX, clientY: e.clientY } : null);
            if (p) start = { x: p.clientX, y: p.clientY, t: performance.now() };
          } catch (_) { }
          __pidLog(label, "start", __pidEventSnapshot(e), {
            scroll: { x: window.scrollX, y: window.scrollY },
          });
        },
        onMove: (e) => {
          if (!start) return;
          try {
            const p =
              e?.touches?.[0] ||
              (typeof e?.clientX === "number" ? { clientX: e.clientX, clientY: e.clientY } : null);
            if (!p) return;
            last = { x: p.clientX, y: p.clientY, t: performance.now() };
            const dx = last.x - start.x;
            const dy = last.y - start.y;
            // log only every ~80ms to avoid console spam
            if (!mkTracker._lastLogAt || performance.now() - mkTracker._lastLogAt > 80) {
              mkTracker._lastLogAt = performance.now();
              __pidLog(label, "move", __pidEventSnapshot(e), {
                dx: Math.round(dx),
                dy: Math.round(dy),
                abs: { dx: Math.round(Math.abs(dx)), dy: Math.round(Math.abs(dy)) },
                scroll: { x: window.scrollX, y: window.scrollY },
              });
            }
          } catch (_) { }
        },
        onEnd: (e) => {
          if (!start) return;
          const end = last || start;
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const dt = Math.max(1, end.t - start.t);
          __pidLog(label, "end", __pidEventSnapshot(e), {
            dx: Math.round(dx),
            dy: Math.round(dy),
            dt: Math.round(dt),
            v: { x: Math.round((dx / dt) * 1000), y: Math.round((dy / dt) * 1000) }, // px/s
            navType: __pidGetNavType(),
            scroll: { x: window.scrollX, y: window.scrollY },
          });
          start = null;
          last = null;
        },
      };
    };

    const attachTo = (el, label) => {
      if (!el || el.__pidDebugBound) return;
      el.__pidDebugBound = true;
      const tr = mkTracker(label);

      const optsPassive = { capture: true, passive: true };
      const optsActive = { capture: true, passive: false };

      // Pointer events (desktop / newer iOS versions)
      el.addEventListener("pointerdown", tr.onStart, optsPassive);
      el.addEventListener("pointermove", tr.onMove, optsPassive);
      el.addEventListener("pointerup", tr.onEnd, optsPassive);
      el.addEventListener("pointercancel", tr.onEnd, optsPassive);

      // Touch events (iOS Safari behavior is often easier to see here)
      el.addEventListener("touchstart", tr.onStart, optsPassive);
      el.addEventListener("touchmove", tr.onMove, optsPassive);
      el.addEventListener("touchend", tr.onEnd, optsPassive);
      el.addEventListener("touchcancel", tr.onEnd, optsPassive);

      // If the browser *would* allow preventing scroll, you'll see cancelable=true here.
      el.addEventListener(
        "touchmove",
        (e) => __pidLog(label, "touchmove cancelability", __pidEventSnapshot(e)),
        optsActive,
      );

      // Opt-in experiment: block native scroll/refresh handoff for predominantly-horizontal gestures.
      // This is ONLY for debugging; enabling this may change native scrolling behavior.
      if (__pidDebugBlockNativeHandoff) {
        let sx = null;
        let sy = null;
        el.addEventListener(
          "touchstart",
          (e) => {
            try {
              const t = e.touches?.[0];
              sx = t ? t.clientX : null;
              sy = t ? t.clientY : null;
            } catch (_) {
              sx = null;
              sy = null;
            }
          },
          optsPassive,
        );
        el.addEventListener(
          "touchmove",
          (e) => {
            try {
              if (!e.cancelable) return;
              const t = e.touches?.[0];
              if (!t || sx === null || sy === null) return;
              const dx = t.clientX - sx;
              const dy = t.clientY - sy;
              const ax = Math.abs(dx);
              const ay = Math.abs(dy);
              // If user intent is horizontal, prevent the browser from treating it like a scroll/pull gesture.
              if (ax > 18 && ax > ay + 6) {
                e.preventDefault();
                __pidLog(label, "blocked native handoff (preventDefault)", {
                  dx: Math.round(dx),
                  dy: Math.round(dy),
                  ax: Math.round(ax),
                  ay: Math.round(ay),
                });
              }
            } catch (_) { }
          },
          optsActive,
        );
      }

      // Scroll tracing
      el.addEventListener(
        "scroll",
        () => {
          try {
            __pidLog(label, "scroll", {
              el: __pidShortEl(el),
              scrollTop: el.scrollTop,
              scrollLeft: el.scrollLeft,
              scrollH: el.scrollHeight,
              clientH: el.clientHeight,
            });
          } catch (_) { }
        },
        optsPassive,
      );
    };

    if (drawer) attachTo(drawer, "drawer");
    mediaLists.forEach((el, i) => attachTo(el, `mediaList#${i}`));

    // Global lifecycle signals for "true refresh" vs bfcache restore
    if (!window.__pidGlobalDebugBound) {
      window.__pidGlobalDebugBound = true;
      // Record a marker so post-reload you can tell which session ended.
      try {
        localStorage.setItem(
          __PID_DEBUG_META_KEY,
          JSON.stringify({
            startedAtUnix: Date.now(),
            navType: __pidGetNavType(),
            href: window.location.href,
            ua: navigator.userAgent,
          }),
        );
      } catch (_) { }

      const runtime = {
        startedAtUnix: Date.now(),
        lastInteractionUnix: null,
        lastInteraction: null,
        lastVisibility: document.visibilityState,
        lastHref: window.location.href,
        // noise counters (errors/unhandled rejections)
        noise: {
          scriptErrorCount: 0,
          unhandledRejectionCount: 0,
          lastScriptErrorUnix: null,
          lastUnhandledRejectionUnix: null,
          lastUnhandledRejectionReason: null,
          lastUnhandledRejectionReasonType: null,
        },
      };

      const writeRuntime = (patch = {}) => {
        try {
          Object.assign(runtime, patch);
          localStorage.setItem(__PID_DEBUG_RUNTIME_KEY, JSON.stringify(runtime));
        } catch (_) { }
      };

      // Dedupe/rate-limit noisy global events so they don't drown the log.
      const mkRateLimiter = ({ windowMs, maxPerWindow }) => {
        let winStart = 0;
        let count = 0;
        return {
          allow(now) {
            if (!winStart || now - winStart > windowMs) {
              winStart = now;
              count = 0;
            }
            count++;
            return count <= maxPerWindow;
          },
          peek() {
            return { winStart, count };
          },
        };
      };

      const scriptErrorLimiter = mkRateLimiter({ windowMs: 3000, maxPerWindow: 1 });
      const rejectionLimiter = mkRateLimiter({ windowMs: 3000, maxPerWindow: 3 });
      let lastRejectionSig = null;
      let lastRejectionSigUnix = 0;

      const recordInteraction = (e) => {
        try {
          const snap = __pidEventSnapshot(e);
          writeRuntime({
            lastInteractionUnix: Date.now(),
            lastInteraction: snap,
            lastHref: window.location.href,
          });
        } catch (_) { }
      };

      // Capture "what was the user doing" right before an unload/reload.
      for (const ev of ["pointerdown", "touchstart", "click", "keydown"]) {
        window.addEventListener(ev, recordInteraction, { capture: true, passive: true });
      }

      // Heartbeat so if Safari kills the tab, we still have a recent timestamp.
      window.setInterval(() => {
        writeRuntime({
          lastVisibility: document.visibilityState,
          lastHref: window.location.href,
        });
      }, 1000);

      window.addEventListener(
        "pageshow",
        (e) => __pidLog("pageshow", { persisted: !!e.persisted, navType: __pidGetNavType() }),
        true,
      );
      window.addEventListener("pagehide", (e) => __pidLog("pagehide", { persisted: !!e.persisted }), true);
      window.addEventListener("visibilitychange", () => __pidLog("visibilitychange", document.visibilityState), true);
      window.addEventListener("beforeunload", () => __pidLog("beforeunload fired"), true);
      window.addEventListener("unload", () => __pidLog("unload fired"), true);
      window.addEventListener("scroll", () => __pidLog("window scroll", { x: window.scrollX, y: window.scrollY }), {
        capture: true,
        passive: true,
      });
      window.addEventListener("popstate", () => __pidLog("popstate", { state: history.state }), true);
      window.addEventListener(
        "hashchange",
        () => __pidLog("hashchange", { hash: window.location.hash }),
        true,
      );

      window.addEventListener(
        "error",
        (e) => {
          try {
            const now = Date.now();
            const msg = e?.message || "";
            const isScriptError = msg === "Script error." || msg === "Script error";

            if (isScriptError) {
              runtime.noise.scriptErrorCount++;
              runtime.noise.lastScriptErrorUnix = now;
              writeRuntime({ noise: runtime.noise });

              if (!scriptErrorLimiter.allow(now)) return;
              __pidLog("window error (Script error.)", {
                message: msg,
                filename: e?.filename,
                lineno: e?.lineno,
                colno: e?.colno,
                total: runtime.noise.scriptErrorCount,
              });
              return;
            }

            __pidLog("window error", {
              message: msg,
              filename: e?.filename,
              lineno: e?.lineno,
              colno: e?.colno,
            });
          } catch (_) { }
        },
        true,
      );
      window.addEventListener(
        "unhandledrejection",
        (e) => {
          try {
            const now = Date.now();
            runtime.noise.unhandledRejectionCount++;
            runtime.noise.lastUnhandledRejectionUnix = now;

            let reasonStr = "";
            let reasonType = typeof e?.reason;
            try {
              if (typeof e?.reason === "string") reasonStr = e.reason;
              else reasonStr = JSON.stringify(e?.reason);
            } catch (_) {
              try {
                reasonStr = String(e?.reason);
              } catch (_) {
                reasonStr = "[unstringifiable]";
              }
            }

            runtime.noise.lastUnhandledRejectionReason = reasonStr;
            runtime.noise.lastUnhandledRejectionReasonType = reasonType;
            writeRuntime({ noise: runtime.noise });

            // Dedupe identical reasons that arrive in a burst (common with 3p scripts).
            const sig = `${reasonType}:${reasonStr}`;
            if (sig === lastRejectionSig && now - lastRejectionSigUnix < 500) {
              return;
            }
            lastRejectionSig = sig;
            lastRejectionSigUnix = now;

            // Rate-limit spam while still keeping a breadcrumb that it happened.
            if (!rejectionLimiter.allow(now)) return;

            __pidLog("unhandledrejection", {
              reason: reasonStr,
              reasonType,
              total: runtime.noise.unhandledRejectionCount,
            });
          } catch (_) { }
        },
        true,
      );
    }
  };

  /* §CLASS
    NOTE: The image drawer feature relies heavily on the swiper bundle
    without it, the pinch, the swipe, the zoom, etc. will ALL BREAK

    I have to assume it comes pre-loaded in the stockMart default theme,
    but if not, and this breaks, double check for /assets/swiper-bundle.min.js & .css

    docs: https://swiperjs.com/swiper-api
  */

  class ProductImageDrawer {
    // §CONSTRUCTOR
    constructor(root) {
      this.root = root;
      const breakpointStr = getComputedStyle(root)
        .getPropertyValue("--pid-mobile-breakpoint")
        .trim();
      this.mobileBreakpointPx = breakpointStr
        ? parseInt(breakpointStr, 10)
        : 850;
      if (!Number.isFinite(this.mobileBreakpointPx))
        this.mobileBreakpointPx = 850;
      const thumbnailGapStr = getComputedStyle(root)
        .getPropertyValue("--pid-thumbnail-gap")
        .trim();
      this.thumbnailGapPx = thumbnailGapStr
        ? parseInt(thumbnailGapStr, 10)
        : 10;
      if (!Number.isFinite(this.thumbnailGapPx)) this.thumbnailGapPx = 10;
      this.drawer = root.querySelector("[data-product-image-drawer]");
      this.backdrop = root.querySelector("[data-pid-backdrop]");
      this.closeBtn = root.querySelector("[data-pid-close]");
      this.tabs = Array.from(root.querySelectorAll("[data-pid-tab]"));
      this.panels = Array.from(root.querySelectorAll("[data-pid-panel]"));
      this.isOpen = false;

      this.activePanel = this.tabs.some((t) => t.dataset.pidTab === "images")
        ? "images"
        : "videos";

      this.swipersByPanel = {
        images: { main: null, thumbs: null },
        videos: { main: null, thumbs: null },
      };

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);

      this.closeBtn?.addEventListener("click", () => this.close());
      this.backdrop?.addEventListener("click", this.onBackdropClick);

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const name = tab.dataset.pidTab;
          if (!name) return;
          this.setActivePanel(name);
        });
      });

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.drawer.addEventListener("pointerdown", this.onPointerDown, {
        passive: true,
        capture: true,
      });

      __pidAttachGestureDebug(this.root);

      this._suppressNextMediaClick = false;
      this._zoomTarget = 1;
      this._zoomButtonActive = false;
      this._docZoomUiBlockersBound = false;
      this._onDocZoomUiBlock = (e) => {
        if (!this.isDesktop()) return;
        if (!e.target?.closest?.(".main-media-zoom-indicator-wrapper")) return;
        if (!this.isOpen) return;
        if (e.type === "pointerdown") {
          this.onZoomControlPointerDown(e);
        }

        if (window.__pidDebugZoom)
          console.log("[pid] blocked", e.type, "at document");
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      this._bindDocZoomUiBlockers = () => {
        if (this._docZoomUiBlockersBound) return;
        this._docZoomUiBlockersBound = true;
        const opts = { capture: true, passive: false };
        for (const ev of [
          "pointerdown",
          "pointerup",
          "pointercancel",
          "mousedown",
          "mouseup",
          "touchstart",
          "touchend",
          "click",
        ]) {
          document.addEventListener(ev, this._onDocZoomUiBlock, opts);
        }
      };
      this._unbindDocZoomUiBlockers = () => {
        if (!this._docZoomUiBlockersBound) return;
        this._docZoomUiBlockersBound = false;
        const opts = { capture: true, passive: false };
        for (const ev of [
          "pointerdown",
          "pointerup",
          "pointercancel",
          "mousedown",
          "mouseup",
          "touchstart",
          "touchend",
          "click",
        ]) {
          document.removeEventListener(ev, this._onDocZoomUiBlock, opts);
        }
      };

      this.onZoomControlPointerDown = (e) => {
        if (!this.isDesktop()) return;

        const zoomIn = e.target.closest("[data-pid-zoom-in]");
        const zoomOut = e.target.closest("[data-pid-zoom-out]");
        if (!zoomIn && !zoomOut) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const btn = zoomIn || zoomOut;
        if (btn?.setPointerCapture) btn.setPointerCapture(e.pointerId);

        this._suppressNextMediaClick = true;
        this._zoomButtonActive = true;

        if (this.activePanel !== "images") return;
        this.initSwipers("images");

        const main = this.swipersByPanel?.images?.main;
        if (!main?.zoom) return;
        const prevAllowClick = main.allowClick;
        main.allowClick = false;

        if (window.__pidDebugZoom) {
          const measured = this.getZoomScale();
          const snapped = this.snapToStep(measured);
          const steps = ProductImageDrawer.ZOOM_STEPS;
          const idx = steps.indexOf(snapped);
          const nextStep = zoomIn
            ? idx < steps.length - 1
              ? steps[idx + 1]
              : snapped
            : idx > 0
              ? steps[idx - 1]
              : snapped;
          console.log("[pid] zoom button pointerdown:", {
            type: zoomIn ? "in" : "out",
            measured,
            _zoomTarget: this._zoomTarget,
            snapped,
            computedNext: nextStep,
          });
        }

        try {
          if (zoomIn) this.zoomStepIn(e);
          else this.zoomStepOut(e);
        } finally {
          requestAnimationFrame(() => {
            window.setTimeout(() => {
              this._zoomButtonActive = false;
              main.allowClick = prevAllowClick;
            }, 80);
          });
        }
      };

      this.showVariantNames = root.dataset.pidShowVariantNames === "true";
      this.showVariantNames = root.dataset.pidShowVariantNames === "true";
      this.variantNameEl = root.querySelector("[data-pid-variant-name]") || null;
      this.variantMap = null;
      const sectionId = root.dataset.productImageDrawerRoot;
      const mapScript = sectionId
        ? root.querySelector(`[data-pid-variant-map="${sectionId}"]`)
        : null;
      if (mapScript) {
        try {
          this.variantMap = JSON.parse(mapScript.textContent);
        } catch (_) { }
      }

      this.drag = {
        active: false,
        startX: 0,
        startY: 0,
        lastY: 0,
        startT: 0,
        locked: null, // 'dismiss' | 'swiper' | 'pan' | null
        panBaseX: 0,
        panBaseY: 0,
      };

      this.root.addEventListener("click", (e) => {
        const thumbSlide = e.target.closest("[data-pid-thumb]");
        if (!thumbSlide) return;

        const panelEl = thumbSlide.closest("[data-pid-panel]");
        const panelName = panelEl?.dataset?.pidPanel;
        if (panelName !== "images" && panelName !== "videos") return;

        if (this.activePanel !== panelName) this.setActivePanel(panelName);

        // Debounced to the latest click: grid arrow-key nav fires one of
        // these per key press (see bindGridKeyboardNav), and a queued-but-
        // not-yet-run callback for an earlier, now-superseded index would
        // otherwise still land and briefly point the main slide (and
        // syncThumbsToMain's tabindex bookkeeping) at a stale item.
        if (this._pidThumbClickTimer) {
          window.clearTimeout(this._pidThumbClickTimer);
        }
        this._pidThumbClickTimer = window.setTimeout(() => {
          this._pidThumbClickTimer = null;
          this.initSwipers(panelName);

          const main = this.swipersByPanel?.[panelName]?.main;
          if (!main) return;

          const idx = parseInt(
            thumbSlide.getAttribute("data-filtered-index"),
            10,
          );
          if (!Number.isFinite(idx)) return;

          this._unlockNavForProgrammatic();
          main.slideTo(idx, 0);
          this.syncThumbsToMain(panelName);
        }, 0);
      });

      this.bindGridKeyboardNav();

      this._ignoreNextPointerUp = false;

      this.root.addEventListener("click", (e) => {
        const shield = e.target.closest("[data-pid-iframe-shield]");
        if (!shield || shield.classList.contains("is-disabled")) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const rect = shield.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const third = rect.width / 3;

        if (x < third) {
          this._ignoreNextPointerUp = true;
          window.setTimeout(() => (this._ignoreNextPointerUp = false), 0);
          this.stopVideoOnNav();
          this._unlockNavForProgrammatic();
          this.getActiveMainSwiper()?.slidePrev?.();
          return;
        }
        if (x > rect.width - third) {
          this._ignoreNextPointerUp = true;
          window.setTimeout(() => (this._ignoreNextPointerUp = false), 0);
          this.stopVideoOnNav();
          this._unlockNavForProgrammatic();
          this.getActiveMainSwiper()?.slideNext?.();
          return;
        }
        this._ignoreNextPointerUp = true;
        window.setTimeout(() => (this._ignoreNextPointerUp = false), 0);
        this.disableVideoShield(shield);
      });
    }

    // §REINIT
    reinit() {
      ["images", "videos"].forEach((panelName) => {
        const state = this.swipersByPanel?.[panelName];
        if (state?.main?.destroy)
          try {
            state.main.destroy();
          } catch (_) { }
        if (state?.thumbs?.destroy)
          try {
            state.thumbs.destroy();
          } catch (_) { }
      });
      this.swipersByPanel = {
        images: { main: null, thumbs: null },
        videos: { main: null, thumbs: null },
      };
      this.isOpen = false;
      this.root?.classList?.remove("pid-is-open");
      document.body?.classList?.remove("pid-scroll-lock");

      this.drawer = this.root?.querySelector("[data-product-image-drawer]");
      this.backdrop = this.root?.querySelector("[data-pid-backdrop]");
      this.closeBtn = this.root?.querySelector("[data-pid-close]");
      this.tabs = Array.from(
        this.root?.querySelectorAll("[data-pid-tab]") || [],
      );
      this.panels = Array.from(
        this.root?.querySelectorAll("[data-pid-panel]") || [],
      );

      this.activePanel = this.tabs.some((t) => t?.dataset?.pidTab === "images")
        ? "images"
        : "videos";

      this.closeBtn?.addEventListener("click", () => this.close());
      this.backdrop?.addEventListener("click", this.onBackdropClick);
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const name = tab.dataset.pidTab;
          if (!name) return;
          this.setActivePanel(name);
        });
      });
      this.drawer?.addEventListener("pointerdown", this.onPointerDown, {
        passive: true,
        capture: true,
      });
    }

    // §ZOOM_CORE
    // Desktop: stepped zoom levels (1x through 3x in 0.25 steps) for fine control
    static ZOOM_STEPS = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];
    static ZOOM_MIN = 1;
    static ZOOM_MAX = 3;

    readActualZoomScale() {
      if (this.activePanel !== "images") return 1;

      const main = this.swipersByPanel?.images?.main;
      const zoom = main?.zoom;
      if (!main || !zoom) return 1;

      const a = Number.isFinite(zoom.currentScale) ? zoom.currentScale : null;
      const b = Number.isFinite(zoom.scale) ? zoom.scale : null;
      let domScale = null;
      try {
        const slide = main.slides?.[main.activeIndex];
        const container = slide?.querySelector(".swiper-zoom-container");
        const t = container ? getComputedStyle(container).transform : null;
        if (t && t !== "none") {
          // matrix(a,b,c,d,tx,ty) => scaleX = a
          const m = t.match(/matrix\(([^)]+)\)/);
          if (m) {
            const parts = m[1].split(",").map((x) => parseFloat(x.trim()));
            if (Number.isFinite(parts[0])) domScale = parts[0];
          }
        }
      } catch (_) { }
      const candidate = a ?? b ?? domScale ?? 1;

      return Number.isFinite(candidate) ? candidate : 1;
    }

    getZoomScale() {
      if (this.activePanel !== "images") return 1;
      return this.readActualZoomScale();
    }

    getCurrentStepIndex() {
      const scale = this.getZoomScale();
      const steps = ProductImageDrawer.ZOOM_STEPS;
      let idx = 0;
      for (let i = 0; i < steps.length; i++) {
        if (scale >= steps[i] - 0.01) idx = i;
        else break;
      }
      return idx;
    }

    getCenterZoomEvent() {
      const main = this.swipersByPanel?.images?.main;
      if (!main) return null;
      const slide = main.slides?.[main.activeIndex];
      const container = slide?.querySelector(".swiper-zoom-container");
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      return {
        pageX: rect.left + rect.width / 2 + window.scrollX,
        pageY: rect.top + rect.height / 2 + window.scrollY,
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
        target: container,
        preventDefault() { },
        stopPropagation() { },
      };
    }

    // Toggle ONLY the slide-direction commit gates. Crucially this does NOT
    // disable Swiper's pointer/touch tracking pipeline (unlike allowTouchMove),
    // so Swiper's own zoom-pan keeps working while horizontal-drag slide
    // commits get blocked. Prior attempts that flipped allowTouchMove broke
    // pan because Swiper's zoom module reads from its touches pipeline.
    _setSwipeNavAllowed(allowed) {
      const main = this.swipersByPanel?.images?.main;
      if (!main) return;
      const next = !!allowed;
      if (main.allowSlideNext === next && main.allowSlidePrev === next) return;
      main.allowSlideNext = next;
      main.allowSlidePrev = next;
      if (window.__pidDebugZoom || __pidDebugEnabled) {
        __pidLog("swipe nav", next ? "unlocked" : "locked", {
          scale: this.readActualZoomScale(),
          activeIndex: main.activeIndex,
        });
      }
    }

    // Programmatic navigation (arrow buttons, thumbnail clicks, keyboard
    // arrows, video-shield edge clicks) must NEVER be blocked by the
    // zoom-time swipe lock. Swiper's slideTo / slideNext / slidePrev all
    // honor allowSlideNext / allowSlidePrev for *programmatic* calls too,
    // not just touch swipes. Without this, after the user zooms in and
    // pans the gates stay closed and every click-driven nav silently
    // no-ops until they zoom back to 1x. We unlock here; if zoom is still
    // >1x, slideChangeTransitionStart resets it as part of the slide commit.
    _unlockNavForProgrammatic() {
      this._setSwipeNavAllowed(true);
    }

    zoomToScale(targetScale, event) {
      const main = this.swipersByPanel?.images?.main;
      const zoom = main?.zoom;
      if (!main || !zoom) return;

      const clamped = Math.max(
        ProductImageDrawer.ZOOM_MIN,
        Math.min(ProductImageDrawer.ZOOM_MAX, targetScale),
      );

      this._zoomTarget = clamped;

      const slide = main.slides?.[main.activeIndex];
      const container = slide?.querySelector(".swiper-zoom-container");
      if (!container) return;

      if (clamped <= 1) {
        try {
          zoom.out();
        } catch (_) { }
        this.drawer.classList.remove("pid-zoomed");
        this.updateZoomReadoutFrom(1);
        this._setSwipeNavAllowed(true);
        return;
      }

      const current = zoom.scale ?? zoom.currentScale ?? 1;
      const x = zoom.currentX ?? 0;
      const y = zoom.currentY ?? 0;

      zoom.scale = clamped;
      zoom.currentScale = clamped;
      zoom.currentX = x;
      zoom.currentY = y;
      zoom.isScaling = false; // Avoid Swiper "cleanup" logic thinking a gesture is in progress

      if (typeof zoom.setTransition === "function") {
        try {
          zoom.setTransition(0);
        } catch (_) { }
      }

      if (typeof zoom.setTransform === "function") {
        try {
          zoom.setTransform(x, y, clamped);
        } catch (_) { }
      } else {
        container.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${clamped})`;
      }

      this.drawer.classList.add("pid-zoomed");
      this.updateZoomReadoutFrom(clamped);
      this._setSwipeNavAllowed(false);
    }

    // §ZOOM_STEPS
    zoomStepIn(event) {
      const measured = this.getZoomScale();
      const base =
        measured > 1.01
          ? measured
          : this._zoomTarget > 1.01
            ? this._zoomTarget
            : measured;

      const current = this.snapToStep(base);
      const steps = ProductImageDrawer.ZOOM_STEPS;
      const idx = steps.indexOf(current);
      const next =
        idx >= 0 && idx < steps.length - 1
          ? steps[idx + 1]
          : steps[steps.length - 1];
      this.zoomToScale(next, event);
    }

    zoomStepOut(event) {
      const measured = this.getZoomScale();
      const base =
        measured > 1.01
          ? measured
          : this._zoomTarget > 1.01
            ? this._zoomTarget
            : measured;

      const current = this.snapToStep(base);
      const steps = ProductImageDrawer.ZOOM_STEPS;
      const idx = steps.indexOf(current);
      const prev = idx > 0 ? steps[idx - 1] : steps[0];
      this.zoomToScale(prev, event);
    }

    updateZoomReadout() {
      if (!this.isDesktop()) return;

      const scale = this.getZoomScale();
      const snapped = this.snapToStep(scale);
      if (scale > 1.01 || this._zoomTarget <= 1.01) {
        this._zoomTarget = snapped;
      }
      this.updateZoomReadoutFrom(snapped);
    }

    updateZoomReadoutFrom(scale) {
      if (!this.isDesktop()) return;
      const s = Number.isFinite(scale) ? scale : 1;
      this.root.querySelectorAll("[data-pid-zoom-readout]").forEach((el) => {
        el.textContent = `${Math.round(s * 100)}%`;
      });
    }

    snapToStep(scale) {
      const steps = ProductImageDrawer.ZOOM_STEPS;
      const s = Number.isFinite(scale) ? scale : 1;
      let best = steps[0];
      let bestDist = Math.abs(s - best);
      for (let i = 1; i < steps.length; i++) {
        const d = Math.abs(s - steps[i]);
        if (d < bestDist) {
          best = steps[i];
          bestDist = d;
        }
      }
      return best;
    }

    getSafeZoomEvent(event) {
      const main = this.swipersByPanel?.images?.main;
      const slide = main?.slides?.[main.activeIndex];
      const container = slide?.querySelector(".swiper-zoom-container");
      if (!container) return this.getCenterZoomEvent() || undefined;

      if (event && event.target && container.contains(event.target)) {
        return event;
      }
      return this.getCenterZoomEvent() || undefined;
    }

    // §ZOOM_EVENTS
    zoomInAtCenter() {
      const main = this.swipersByPanel?.images?.main;
      if (!main?.zoom) return;

      const slide = main.slides?.[main.activeIndex];
      const container = slide?.querySelector(".swiper-zoom-container");
      if (!container) {
        main.zoom.in();
        return;
      }

      const rect = container.getBoundingClientRect();
      const fakeEvent = {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
        preventDefault() { },
        stopPropagation() { },
      };

      main.zoom.in(fakeEvent);
    }

    isDesktop() {
      return window.matchMedia(`(min-width: ${this.mobileBreakpointPx}px)`)
        .matches;
    }

    isTouch() {
      try {
        return window.matchMedia("(pointer: coarse)").matches;
      } catch (_) {
        return false;
      }
    }

    toggleZoomAtEvent(e) {
      if (this.activePanel !== "images") return;
      if (e.target.closest(".main-media-zoom-indicator-wrapper")) return;
      const main = this.swipersByPanel?.images?.main;
      if (!main?.zoom) return;

      const interactive = e.target.closest(`
        button, a, input, select, textarea,
        [data-pid-zoom-in], [data-pid-zoom-out],
        [data-pid-thumb], [data-pid-thumb-prev], [data-pid-thumb-next],
        [data-pid-tab], [data-pid-close]
      `);
      if (interactive) return;

      if (this.isDesktop()) {
        this.zoomStepIn(e);
        return;
      }

      if (this.isTouch()) return; // double-tap handled in _attachTouchZoom
      if (this.isZoomed()) main.zoom.out();
      else main.zoom.in(e);
      this.drawer.classList.toggle("pid-zoomed", this.isZoomed());
    }

    // §VIDEO_SHIELDS
    stopVideoOnNav() {
      if (!this.root) return;
      this.root.querySelectorAll("video").forEach((video) => {
        try {
          video.pause();
        } catch (_) { }
      });

      if (window.pauseAllMedia) {
        try {
          window.pauseAllMedia();
        } catch (_) { }
      }
    }

    HINT_FADE_MS = 1500;
    SHIELD_REENABLE_MS = 4000;

    scheduleHintFade(shield) {
      if (!shield || shield.classList.contains("is-disabled")) return;
      if (shield._pidHintFadeTimer) clearTimeout(shield._pidHintFadeTimer);
      shield._pidHintFadeTimer = window.setTimeout(() => {
        shield._pidHintFadeTimer = null;
        shield.classList.add("pid-iframe-shield--hint-faded");
      }, this.HINT_FADE_MS);
    }

    clearShieldReenable(shield) {
      if (!shield) return;
      if (shield._pidReenableTimer) {
        clearTimeout(shield._pidReenableTimer);
        shield._pidReenableTimer = null;
      }
      if (shield._pidReenable) {
        document.removeEventListener("pointerdown", shield._pidReenable, true);
        shield._pidReenable = null;
      }
    }

    reenableVideoShield(shield) {
      if (!shield) return;
      this.clearShieldReenable(shield);
      shield.classList.remove("is-disabled");
      shield.classList.remove("pid-iframe-shield--hint-faded");
      this.scheduleHintFade(shield);
    }

    disableVideoShield(shield) {
      if (!shield) return;
      if (shield._pidHintFadeTimer) {
        clearTimeout(shield._pidHintFadeTimer);
        shield._pidHintFadeTimer = null;
      }
      this.clearShieldReenable(shield);
      shield.classList.add("is-disabled");

      const reenable = (ev) => {
        if (ev.target.closest("iframe")) return;
        this.reenableVideoShield(shield);
      };
      shield._pidReenable = reenable;
      document.addEventListener("pointerdown", reenable, true);

      shield._pidReenableTimer = window.setTimeout(() => {
        shield._pidReenableTimer = null;
        this.reenableVideoShield(shield);
      }, this.SHIELD_REENABLE_MS);
    }

    resetVideoShieldsForPanel(panelName) {
      const panel = this.root.querySelector(`[data-pid-panel="${panelName}"]`);
      if (!panel) return;
      panel.querySelectorAll("[data-pid-iframe-shield]").forEach((shield) => {
        this.clearShieldReenable(shield);
        if (shield._pidHintFadeTimer) {
          clearTimeout(shield._pidHintFadeTimer);
          shield._pidHintFadeTimer = null;
        }
        shield.classList.remove("is-disabled");
        shield.classList.remove("pid-iframe-shield--hint-faded");
      });
    }

    applyVideoShieldStateForCurrentSlide(panelName) {
      if (panelName !== "videos") return;
      this.resetVideoShieldsForPanel(panelName);
      const main = this.swipersByPanel?.[panelName]?.main;
      if (!main) return;
      const activeSlide = main.slides?.[main.activeIndex];
      const shield = activeSlide?.querySelector("[data-pid-iframe-shield]");
      if (shield) this.scheduleHintFade(shield);
    }

    // §PANEL
    hasPanel(panelName) {
      const panel = this.root.querySelector(`[data-pid-panel="${panelName}"]`);
      if (!panel) return false;
      const mediaListEl = panel.querySelector("[data-pid-media-list]");
      const slideCount =
        mediaListEl?.querySelectorAll(".swiper-wrapper > .swiper-slide")
          ?.length ?? 0;

      return slideCount > 0;
    }

    goToPanel(panelName, index = 0) {
      this.setActivePanel(panelName);

      window.setTimeout(() => {
        this.initSwipers(panelName);
        const main = this.swipersByPanel?.[panelName]?.main;
        if (!main) return;
        main.slideTo(index, 0);
        this.syncThumbsToMain(panelName);
      }, 0);
    }

    wrapForward(panelName) {
      const main = this.swipersByPanel?.[panelName]?.main;
      if (!main) return;
      if (panelName === "images" && this.hasPanel("videos")) {
        this.goToPanel("videos", 0);
        return;
      }
      this._unlockNavForProgrammatic();
      main.slideTo(0, 0);
      this.syncThumbsToMain(panelName);
    }

    wrapBackward(panelName) {
      const main = this.swipersByPanel?.[panelName]?.main;
      if (!main) return;

      if (panelName === "videos" && this.hasPanel("images")) {
        this.goToPanel(
          "images",
          Math.max(0, this.swipersByPanel.images.main?.slides?.length - 1),
        );
        return;
      }

      this._unlockNavForProgrammatic();
      main.slideTo(Math.max(0, main.slides.length - 1), 0);
      this.syncThumbsToMain(panelName);
    }

    // §NAV
    bindMainNav(panelName, thumbPrev, thumbNext) {
      const state =
        this.swipersByPanel[panelName] || (this.swipersByPanel[panelName] = {});

      const onPrev = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const main = this.swipersByPanel?.[panelName]?.main;
        if (!main) return;

        this.stopVideoOnNav();
        this._unlockNavForProgrammatic();
        if (main.isBeginning) this.wrapBackward(panelName);
        else main.slidePrev();
      };

      const onNext = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const main = this.swipersByPanel?.[panelName]?.main;
        if (!main) return;

        this.stopVideoOnNav();
        this._unlockNavForProgrammatic();

        if (main.isEnd) this.wrapForward(panelName);
        else main.slideNext();
      };

      if (thumbPrev) {
        if (thumbPrev.__pidOnClickPrev) {
          thumbPrev.removeEventListener("click", thumbPrev.__pidOnClickPrev);
        }
        thumbPrev.__pidOnClickPrev = onPrev;
        thumbPrev.addEventListener("click", onPrev);
      }

      if (thumbNext) {
        if (thumbNext.__pidOnClickNext) {
          thumbNext.removeEventListener("click", thumbNext.__pidOnClickNext);
        }
        thumbNext.__pidOnClickNext = onNext;
        thumbNext.addEventListener("click", onNext);
      }
    }

    // Grid thumbnails use roving tabindex: only the thumbnail matching the
    // main swiper's active slide is a tab stop (tabindex="0"), every other
    // one is tabindex="-1". This is the single place that flips it, so
    // however the active slide changed (thumb click, grid arrow-nav, main
    // swiper swipe/arrow-key), Tab always lands on whichever thumbnail is
    // actually on screen — keyboard focus and the displayed image can't
    // drift apart the way they did when the grid items were plain divs.
    //
    // That guarantee only covers Tab presses that arrive from *outside* the
    // grid, though. If a thumbnail already has real DOM focus and the active
    // slide then changes via some other route (dragging/swiping the main
    // image, the mobile prev/next nav buttons, the global arrow-key handler
    // in onKeyDown), flipping that thumbnail to tabindex="-1" does not move
    // focus off of it — the focus ring is left stranded on a stale, no-
    // longer-tabbable item while the active-image outline moves elsewhere.
    // A later Tab then resumes from that stale spot instead of the current
    // image, which reads as focus "jumping back". So: whenever focus is
    // already inside this grid, move it to the new active item too, keeping
    // focus, tabindex and the displayed image permanently in lockstep.
    syncThumbsToMain(panelName) {
      const { main, thumbs } = this.swipersByPanel?.[panelName] || {};
      if (!main) return;

      const idx = main.realIndex ?? main.activeIndex ?? 0;

      if (thumbs) {
        thumbs.slides?.forEach((slideEl) => {
          const slideIdx = parseInt(
            slideEl.getAttribute("data-filtered-index"),
            10,
          );
          slideEl.classList.toggle("pid-thumb-active", slideIdx === idx);
        });
        thumbs.slideTo?.(idx);
      }

      const panel = this.root.querySelector(`[data-pid-panel="${panelName}"]`);

      // Swiper never removes off-screen slides from the DOM, so each
      // slide's own focusable controls (zoom buttons here; video/iframe
      // controls in the video panel) stay natively tabbable regardless of
      // which slide is active. Swiper's built-in a11y module (on by
      // default) listens for focus landing anywhere inside the swiper and
      // forces slideTo() on whatever slide it finds — so tabbing past the
      // grid into slide 0's (still-tabbable) zoom button was silently
      // snapping the main image back to the first slide, undoing whatever
      // the grid/arrow-key selection had just set. Only the active slide's
      // controls should ever be reachable by Tab.
      const mediaListEl = panel?.querySelector("[data-pid-media-list]");
      mediaListEl?.querySelectorAll(".swiper-slide").forEach((slideEl) => {
        const slideIdx = parseInt(slideEl.getAttribute("data-filtered-index"), 10);
        const isActive = slideIdx === idx;
        slideEl
          .querySelectorAll(
            "[data-pid-zoom-in], [data-pid-zoom-out], video, [data-pid-iframe-enable]",
          )
          .forEach((el) => {
            el.setAttribute("tabindex", isActive ? "0" : "-1");
          });
      });

      // Sync grid thumbnails
      const gridEl = panel?.querySelector("[data-pid-grid]");
      if (gridEl) {
        const focusWasInGrid = gridEl.contains(document.activeElement);
        gridEl.querySelectorAll("[data-pid-thumb]").forEach((el) => {
          const elIdx = parseInt(el.getAttribute("data-filtered-index"), 10);
          const isActive = elIdx === idx;
          el.classList.toggle("pid-thumb-active", isActive);
          el.setAttribute("aria-current", isActive ? "true" : "false");
          el.setAttribute("tabindex", isActive ? "0" : "-1");
        });
        const activeGridItem = gridEl.querySelector(
          `[data-filtered-index="${idx}"]`,
        );
        activeGridItem?.scrollIntoView?.({ block: "nearest" });
        if (
          focusWasInGrid &&
          activeGridItem &&
          document.activeElement !== activeGridItem
        ) {
          activeGridItem.focus({ preventScroll: true });
        }
      }
    }

    // Arrow-key navigation within the desktop grid thumbnails (roving
    // tabindex pattern). Moving focus also activates the thumbnail — the
    // same "click" codepath a mouse/Enter activation uses — so the focus
    // ring and the selected/displayed image always move together instead
    // of tracking two independent states.
    bindGridKeyboardNav() {
      const NAV_KEYS = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];

      this.root.addEventListener("keydown", (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (!NAV_KEYS.includes(e.key)) return;

        const gridBtn = e.target.closest("[data-pid-grid] [data-pid-thumb]");
        if (!gridBtn) return;

        const grid = gridBtn.closest("[data-pid-grid]");
        const items = Array.from(grid.querySelectorAll("[data-pid-thumb]"));
        const curIdx = items.indexOf(gridBtn);
        if (curIdx < 0) return;

        const cols = this._gridColumnCount(items);
        let nextIdx = curIdx;

        if (e.key === "ArrowRight") nextIdx = curIdx + 1;
        else if (e.key === "ArrowLeft") nextIdx = curIdx - 1;
        else if (e.key === "ArrowDown") nextIdx = curIdx + cols;
        else if (e.key === "ArrowUp") nextIdx = curIdx - cols;
        else if (e.key === "Home") nextIdx = 0;
        else if (e.key === "End") nextIdx = items.length - 1;

        if (nextIdx === curIdx || nextIdx < 0 || nextIdx >= items.length) return;

        // Stop here so window's onKeyDown (bare ArrowLeft/ArrowRight pages
        // the main swiper) never sees this key — inside the grid, arrow
        // keys belong to the grid instead of the whole drawer.
        e.preventDefault();
        e.stopPropagation();

        // Move the roving tab stop (and the active/aria-current styling)
        // synchronously, in the same turn as focus — don't wait on the
        // click below, whose actual slide change (and the syncThumbsToMain
        // call that normally owns this bookkeeping) is deferred a tick to
        // let Swiper catch up. Without this, a real Tab/Shift+Tab press
        // landing in the gap between an arrow key press and that deferred
        // sync would still see tabindex="0" on the *previous* item — the
        // tab stop reads as "jumping back" instead of following focus.
        // syncThumbsToMain() still re-confirms the same item once the main
        // slide actually changes; it's the source of truth going forward.
        items[curIdx].setAttribute("tabindex", "-1");
        items[curIdx].setAttribute("aria-current", "false");
        items[curIdx].classList.remove("pid-thumb-active");
        items[nextIdx].setAttribute("tabindex", "0");
        items[nextIdx].setAttribute("aria-current", "true");
        items[nextIdx].classList.add("pid-thumb-active");

        items[nextIdx].focus();
        items[nextIdx].click();
      });
    }

    // Column count read from actual layout rather than hardcoded, so this
    // keeps working if the grid's CSS column count ever changes.
    _gridColumnCount(items) {
      if (items.length < 2) return items.length || 1;
      const firstTop = items[0].offsetTop;
      const idx = items.findIndex((el) => el.offsetTop !== firstTop);
      return idx > 0 ? idx : items.length;
    }

    syncMainCarouselToActiveSlide(panelName) {
      try {
        const main = this.swipersByPanel?.[panelName]?.main;
        if (!main) return;
        const activeSlide = main.slides?.[main.activeIndex];
        const mediaId = activeSlide?.dataset?.mediaId;
        if (!mediaId) return;

        document.querySelectorAll(".js-media-list").forEach((el) => {
          if (!el.swiper) return;
          const idx = Array.from(el.swiper.slides).findIndex((slide) => {
            const m = (slide.getAttribute("data-media-id") || "").match(/(\d+)$/);
            return m && m[1] === mediaId;
          });
          if (idx >= 0) el.swiper.slideTo(idx, 0);
        });
      } catch (_) { }
    }

    // §VARIANT
    updateVariantDisplay() {
      if (!this.variantMap || !this.variantNameEl) return;
      const main = this.swipersByPanel?.images?.main;
      if (!main) return;
      const activeSlide = main.slides?.[main.activeIndex];
      const mediaId = activeSlide?.dataset?.mediaId;
      const variant = mediaId ? this.variantMap[mediaId] : null;
      this.variantNameEl.textContent = variant ? variant.title : "";
      if (variant && this.showVariantNames) {
        this.selectVariantOnPage(variant.options);
      }
    }

    selectVariantOnPage(options) {
      const selector = document.querySelector("variant-selects, variant-radios");
      if (!selector) return;

      const selects = Array.from(selector.querySelectorAll("select"));
      const radioFieldsets = Array.from(
        selector.querySelectorAll(".js-radio-colors"),
      );

      let optIdx = 0;
      selects.forEach((sel) => {
        if (options?.[optIdx] !== undefined) sel.value = options[optIdx++];
      });
      radioFieldsets.forEach((fieldset) => {
        if (options?.[optIdx] !== undefined) {
          const radio = Array.from(fieldset.querySelectorAll("input")).find(
            (r) => r.value === options[optIdx],
          );
          if (radio) radio.checked = true;
          optIdx++;
        }
      });

      selector.dispatchEvent(new Event("change", { bubbles: true }));
    }

    getActiveMainSwiper() {
      return this.swipersByPanel?.[this.activePanel]?.main || null;
    }

    setActivePanel(panelName) {
      if (!panelName) return;
      if (panelName !== "images" && panelName !== "videos") return;

      this.activePanel = panelName;

      this.tabs.forEach((tab) => {
        tab.setAttribute(
          "aria-selected",
          tab.dataset.pidTab === panelName ? "true" : "false",
        );
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.pidPanel !== panelName;
      });

      this.stopVideoOnNav();

      if (panelName !== "images") {
        this.drawer.classList.remove("pid-zoomed");
        this.root.querySelectorAll("[data-pid-zoom-readout]").forEach((el) => {
          el.textContent = "100%";
        });
      }

      window.setTimeout(() => {
        this.initSwipers(panelName);
        const { main, thumbs } = this.swipersByPanel[panelName];
        thumbs?.update?.();
        main?.update?.();
      }, 0);
    }

    // §SWIPER_INIT
    initSwipers(panelName) {
      if (!window.Swiper) return;

      const panel = this.root.querySelector(`[data-pid-panel="${panelName}"]`);
      if (!panel) return;

      const mediaListEl = panel.querySelector("[data-pid-media-list]");
      const sublistEl = panel.querySelector("[data-pid-sublist]");
      const thumbPrev = panel.querySelector("[data-pid-thumb-prev]");
      const thumbNext = panel.querySelector("[data-pid-thumb-next]");
      if (!mediaListEl || !sublistEl) return;
      if (mediaListEl.swiper || sublistEl.swiper) {
        const state =
          this.swipersByPanel[panelName] ||
          (this.swipersByPanel[panelName] = {});
        state.main = mediaListEl.swiper || state.main;
        state.thumbs = sublistEl.swiper || state.thumbs;
        this.bindMainNav(panelName, thumbPrev, thumbNext);
        return;
      }

      const setThumbCenterClass = (sw) => {
        const locked =
          sw.isLocked ?? (!sw.allowSlidePrev && !sw.allowSlideNext);
        sublistEl.classList.toggle("pid-thumbs-centered", !!locked);
      };

      const thumbsSwiper = new Swiper(sublistEl, {
        slidesPerView: "auto",
        spaceBetween: this.thumbnailGapPx,
        direction: "horizontal",
        freeMode: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,

        watchOverflow: true,
        centerInsufficientSlides: true,

        preventClicks: false,
        preventClicksPropagation: false,

        on: {
          init: setThumbCenterClass,
          resize: setThumbCenterClass,
          update: setThumbCenterClass,
        },
      });

      let touchStartIndex = 0;
      let touchStartX = 0;
      let touchStartY = 0;

      const mainSwiper = new Swiper(mediaListEl, {
        slidesPerView: 1,
        spaceBetween: 2,
        autoHeight: false,
        observer: true,
        observeParents: true,
        zoom:
          panelName === "images" && !__pidDebugDisableZoom && !this.isTouch()
            ? { maxRatio: 3, minRatio: 1, toggle: false }
            : false,
        preventClicks: false,
        preventClicksPropagation: false,

        on: {
          init: (swiper) => {
            this.syncThumbsToMain(panelName);
            this.bindMainNav(panelName, thumbPrev, thumbNext);
            if (panelName === "images" && !__pidDebugDisableZoom) {
              swiper.on("zoomChange", () => {
                const scale = this.readActualZoomScale();
                if (window.__pidDebugZoom)
                  console.log("[pid] swiper zoomChange:", { scale });

                this.drawer.classList.toggle("pid-zoomed", scale > 1.01);
                if (this.isDesktop()) {
                  this.root
                    .querySelectorAll("[data-pid-zoom-readout]")
                    .forEach((el) => {
                      el.textContent = `${Math.round(scale * 100)}%`;
                    });
                }

                if (this.isDesktop() && !this._zoomButtonActive) {
                  this._zoomTarget = this.snapToStep(scale);
                }

                // Slide-direction gates ONLY. Touch pipeline stays live so
                // Swiper's zoom-pan keeps tracking; only the slide commit
                // is blocked while zoomed.
                this._setSwipeNavAllowed(scale <= 1.01);
              });
              if (window.__pidDebugZoom) {
                swiper.on("tap", () => console.log("[pid] swiper tap"));
                swiper.on("doubleTap", () =>
                  console.log("[pid] swiper doubleTap"),
                );
                swiper.on("click", (_, e) =>
                  console.log("[pid] swiper click", e?.target),
                );
                if (swiper.touches) {
                  swiper.on("touchEnd", () =>
                    console.log("[pid] swiper touchEnd"),
                  );
                }
              }
            }
            if (panelName === "videos") {
              window.setTimeout(
                () => this.applyVideoShieldStateForCurrentSlide(panelName),
                0,
              );
            }
          },

          slideChange: () => {
            this.stopVideoOnNav();
            this.pauseMediaInDrawer?.();
            if (window.pauseAllMedia) window.pauseAllMedia();
            this.syncThumbsToMain(panelName);
            this.applyVideoShieldStateForCurrentSlide(panelName);
            if (panelName === "images") this.updateVariantDisplay();
            this.syncMainCarouselToActiveSlide(panelName);
          },

          slideChangeTransitionStart: (swiper) => {
            if (panelName !== "images") return;
            if (!swiper?.zoom || __pidDebugDisableZoom) return;

            try {
              swiper.zoom.out();
            } catch (_) { }
            this.drawer.classList.remove("pid-zoomed");
            this.root
              .querySelectorAll("[data-pid-zoom-readout]")
              .forEach((el) => {
                el.textContent = "100%";
              });
            // Slide just changed (zoom is reset to 1x); make sure the new
            // slide is swipeable again. Symmetric with the zoom-out branch
            // in zoomToScale.
            this._setSwipeNavAllowed(true);
          },

          touchStart: (swiper) => {
            touchStartIndex = swiper.activeIndex;
            touchStartX = swiper.touches?.startX ?? 0;
            touchStartY = swiper.touches?.startY ?? 0;
            if (__pidDebugEnabled) {
              __pidLog("swiper touchStart", {
                panelName,
                activeIndex: swiper.activeIndex,
                isBeginning: swiper.isBeginning,
                isEnd: swiper.isEnd,
                allowTouchMove: swiper.allowTouchMove,
                start: { x: Math.round(touchStartX), y: Math.round(touchStartY) },
              });
            }
          },

          touchEnd: (swiper) => {
            const didNotMove = swiper.activeIndex === touchStartIndex;
            if (__pidDebugEnabled) {
              const endX = swiper.touches?.currentX ?? touchStartX;
              const endY = swiper.touches?.currentY ?? touchStartY;
              const dx = endX - touchStartX;
              const dy = endY - touchStartY;
              __pidLog("swiper touchEnd", {
                panelName,
                activeIndex: swiper.activeIndex,
                startIndex: touchStartIndex,
                didNotMove,
                isBeginning: swiper.isBeginning,
                isEnd: swiper.isEnd,
                end: { x: Math.round(endX), y: Math.round(endY) },
                d: { dx: Math.round(dx), dy: Math.round(dy) },
              });
            }
            if (!didNotMove) return;

            const endX = swiper.touches?.currentX ?? touchStartX;
            const endY = swiper.touches?.currentY ?? touchStartY;
            const dx = endX - touchStartX;
            const dy = endY - touchStartY;

            const absDx = Math.abs(dx);
            const absDy = Math.abs(dy);

            if (absDx < 60 || absDx <= absDy) return;
            if (swiper.isEnd && dx < 0) {
              this.wrapForward(panelName);
              return;
            }
            if (swiper.isBeginning && dx > 0) {
              this.wrapBackward(panelName);
              return;
            }
          },
        },
      });

      if (panelName === "images") {
        if (__pidDebugDisableZoom) {
          // Debug isolation: skip click-to-zoom and wheel zoom bindings.
          this.drawer?.classList?.remove("pid-zoomed");
          this.root?.querySelectorAll?.("[data-pid-zoom-readout]")?.forEach?.((el) => {
            el.textContent = "100%";
          });
        }
        if (!mediaListEl.__pidWheelZoomBound) {
          mediaListEl.__pidWheelZoomBound = true;
          mediaListEl.addEventListener(
            "wheel",
            (e) => {
              if (!this.isDesktop() || this.activePanel !== "images") return;
              if (__pidDebugDisableZoom) return;
              const main = this.swipersByPanel?.images?.main;
              if (!main?.zoom) return;
              e.preventDefault();
              if (e.deltaY < 0) this.zoomStepIn(e);
              else this.zoomStepOut(e);
            },
            { passive: false },
          );
        }

        if (!mediaListEl.__pidClickToZoomBound) {
          mediaListEl.__pidClickToZoomBound = true;

          mediaListEl.addEventListener("click", (e) => {
            if (__pidDebugDisableZoom) return;
            if (this._suppressNextMediaClick) {
              if (window.__pidDebugZoom) {
                console.log(
                  "[pid] mediaListEl click: suppressed (_suppressNextMediaClick)",
                );
              }
              this._suppressNextMediaClick = false;
              return;
            }
            if (this._zoomButtonActive) {
              if (window.__pidDebugZoom) {
                console.log(
                  "[pid] mediaListEl click: suppressed (_zoomButtonActive)",
                );
              }
              return;
            }
            if (e.target.closest(".main-media-zoom-indicator-wrapper")) return;

            const sw = this.swipersByPanel?.images?.main;
            if (!sw || sw.allowClick === false) return;

            this.toggleZoomAtEvent(e);
          });
        }
      }

      if (panelName === "images") {
        if (!mediaListEl.__pidDblClickBound) {
          mediaListEl.__pidDblClickBound = true;

          mediaListEl.addEventListener("dblclick", (e) => {
            if (__pidDebugDisableZoom) return;
            if (!this.isDesktop()) return;
            if (e.target.closest(".main-media-zoom-indicator-wrapper")) return;
            if (this._zoomButtonActive) return;

            const main = this.swipersByPanel?.images?.main;
            if (!main?.zoom) return;

            if (this.isZoomed()) {
              this.zoomToScale(1);
              this._zoomTarget = 1;
              this.drawer.classList.remove("pid-zoomed");
              this.updateZoomReadoutFrom(1);
              requestAnimationFrame(() => this.updateZoomReadoutFrom(1));
              return;
            }
            this.zoomToScale(2, e);
            this.drawer.classList.add("pid-zoomed");
            this.updateZoomReadoutFrom(2);
          });
        }
      }

      this.swipersByPanel[panelName] = {
        main: mainSwiper,
        thumbs: thumbsSwiper,
        _navBound: false,
      };

      this.bindMainNav(panelName, thumbPrev, thumbNext);

      if (panelName === "images" && !__pidDebugDisableZoom && this.isTouch()) {
        this._attachTouchZoom(mediaListEl, mainSwiper);
      }
    }

    // §SCROLL_LOCK
    lockScroll() {
      document.documentElement.classList.add("pid-scroll-lock");
      document.body.style.overflow = "hidden";
    }

    unlockScroll() {
      document.documentElement.classList.remove("pid-scroll-lock");
      document.body.style.overflow = "";
    }

    // §OPEN_CLOSE
    open({ index, panel } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this._bindDocZoomUiBlockers();
      if (__pidDebugEnabled) __pidLog("open()", { index, panel, activePanel: this.activePanel });
      // Unhide and force a reflow before adding pid-is-open, so the drawer
      // starts from its closed translateY(100%) and the slide-in transition
      // runs (going from display:none straight to the open state would skip it).
      this.drawer.hidden = false;
      this.backdrop.hidden = false;
      void this.drawer.offsetHeight;
      document.documentElement.classList.add("pid-is-open");
      this.lockScroll();
      if (__pidDebugEnabled && __pidDebugHardScrollLock) __pidHardLock.apply();
      this.drawer.focus({ preventScroll: true });

      window.addEventListener("keydown", this.onKeyDown);

      if (panel) this.setActivePanel(panel);

      // sync() runs twice: once immediately, and again after the drawer's
      // open transition finishes (~220ms) so Swiper can re-measure slide
      // widths against the drawer's final layout. The initial-index jump
      // (main.slideTo(index, 0)) must only happen on whichever of those two
      // calls runs first, though — it applies the slide the drawer was
      // *opened* on, and a keyboard/mouse user can easily navigate to a
      // different slide inside that ~260ms gap. Re-applying it on the
      // second call would silently snap the drawer (image, roving
      // tabindex, and focus — syncThumbsToMain moves focus too) back to
      // the original opening slide, undoing whatever the user just did.
      let initialIndexApplied = false;
      const sync = () => {
        this.initSwipers(this.activePanel);
        const { main, thumbs } = this.swipersByPanel[this.activePanel];
        thumbs?.update?.();
        main?.update?.();
        if (!initialIndexApplied && Number.isInteger(index) && main) {
          initialIndexApplied = true;
          main.slideTo(index, 0);
          this.syncThumbsToMain(this.activePanel);
        }
        this.applyVideoShieldStateForCurrentSlide(this.activePanel);
        if (this.activePanel === "images") this.updateVariantDisplay();
      };
      this.drawer.classList.toggle("pid-zoomed", this.isZoomed());
      this.updateZoomReadout();
      window.setTimeout(sync, 0);
      window.setTimeout(sync, 260);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this._unbindDocZoomUiBlockers();
      if (__pidDebugEnabled) __pidLog("close()");

      this.stopVideoOnNav();
      document.documentElement.classList.remove("pid-is-open");

      this.root
        .querySelectorAll("[data-pid-iframe-shield]")
        .forEach((shield) => {
          this.clearShieldReenable(shield);
          if (shield._pidHintFadeTimer) {
            clearTimeout(shield._pidHintFadeTimer);
            shield._pidHintFadeTimer = null;
          }
        });

      window.setTimeout(() => {
        if (this.isOpen) return;
        this.drawer.hidden = true;
        this.backdrop.hidden = true;
      }, 240);

      this.unlockScroll();
      if (__pidDebugEnabled && __pidDebugHardScrollLock) __pidHardLock.release();
      window.removeEventListener("keydown", this.onKeyDown);
    }

    onBackdropClick() {
      this.close();
    }

    // The dialog has no native focus trap, so Tab/Shift+Tab at the edge of
    // its focusable controls would otherwise walk straight into the
    // underlying page (still in the DOM, just visually covered by the
    // backdrop) — a keyboard user tabbing past the last control lands on
    // invisible content with no indication where focus went. Below the
    // grid's desktop breakpoint this is *especially* confusing: the grid
    // is display:none there (mobile falls back to a swipeable, non-
    // keyboard-navigable thumbnail strip), so there are only a couple of
    // controls before Tab would otherwise escape.
    _getFocusableInDrawer() {
      const selector = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "video[controls]",
        "[tabindex]",
      ].join(",");
      return Array.from(this.drawer.querySelectorAll(selector)).filter((el) => {
        if (el.getAttribute("tabindex") === "-1") return false;
        if (el.closest("[hidden]")) return false;
        if (el.offsetParent === null) return false;
        if (getComputedStyle(el).visibility === "hidden") return false;
        return true;
      });
    }

    _trapFocus(e) {
      const focusable = this._getFocusableInDrawer();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (e.shiftKey) {
        if (current === this.drawer || current === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last) {
        e.preventDefault();
        first.focus();
      }
    }

    onKeyDown(e) {
      if (e.key === "Escape") this.close();
      if (e.key === "Tab") this._trapFocus(e);
      const swiper = this.getActiveMainSwiper();
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        this._unlockNavForProgrammatic();
      }
      if (e.key === "ArrowLeft") swiper?.slidePrev?.();
      if (e.key === "ArrowRight") swiper?.slideNext?.();
      if (this.isDesktop() && this.activePanel === "images" && swiper?.zoom) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          this.zoomStepIn();
        } else if (e.key === "-") {
          e.preventDefault();
          this.zoomStepOut();
        }
      }
    }

    isZoomed() {
      if (this.activePanel !== "images") return false;
      const main = this.getActiveMainSwiper();
      const zoom = main?.zoom;
      const scale = zoom?.scale ?? zoom?.currentScale ?? 1;
      return scale > 1.01;
    }

    // §DRAG
    onPointerDown(e) {
      if (!this.isOpen) return;
      if (e.target.closest(".main-media-zoom-indicator-wrapper")) return;

      if (e.pointerType === "mouse" && e.button !== 0) return;

      const interactive = e.target.closest(`
        button, a, input, select, textarea, video, iframe,
        [data-pid-iframe-enable], [data-pid-iframe-shield],
        [data-pid-zoom-in], [data-pid-zoom-out],
        [data-pid-thumb], [data-pid-thumb-prev], [data-pid-thumb-next],
        [data-pid-tab]`);
      if (interactive) return;

      if (this.isZoomed()) {
        if (!this.isTouch() && this.activePanel === "images") {
          const main = this.swipersByPanel?.images?.main;
          const zoom = main?.zoom;
          if (zoom) {
            // Belt + suspenders: lock slide nav at gesture start in case
            // zoomChange/zoomToScale didn't (or got flipped back by a race
            // with Swiper internals between zoom-in and the first drag).
            this._setSwipeNavAllowed(false);
            this.drag.active = true;
            this.drag.locked = "pan";
            this.drag.startX = e.clientX;
            this.drag.startY = e.clientY;
            this.drag.panBaseX = zoom.currentX ?? 0;
            this.drag.panBaseY = zoom.currentY ?? 0;
            if (__pidDebugEnabled || window.__pidDebugZoom) {
              __pidLog("pan start", {
                start: { x: Math.round(e.clientX), y: Math.round(e.clientY) },
                base: { x: Math.round(this.drag.panBaseX), y: Math.round(this.drag.panBaseY) },
                scale: zoom.scale ?? zoom.currentScale ?? 1,
                allowSlideNext: main.allowSlideNext,
                allowSlidePrev: main.allowSlidePrev,
                allowTouchMove: main.allowTouchMove,
              });
            }
            this.drawer.setPointerCapture?.(e.pointerId);
            document.addEventListener("pointermove", this.onPointerMove, { passive: false, capture: true });
            document.addEventListener("pointerup", this.onPointerUp, { passive: true, capture: true });
            document.addEventListener("pointercancel", this.onPointerUp, { passive: true, capture: true });
          }
        }
        return;
      }

      if (__pidDebugEnabled) {
        __pidLog("drawer pointerdown (drag start candidate)", __pidEventSnapshot(e), {
          isZoomed: this.isZoomed(),
          activePanel: this.activePanel,
          touchAction: this.drawer?.style?.touchAction,
          scroll: { x: window.scrollX, y: window.scrollY },
        });
      }

      this.drag.active = true;
      this.drag.locked = null;
      this.drag.startX = e.clientX;
      this.drag.startY = e.clientY;
      this.drag.lastY = e.clientY;
      this.drag.startT = performance.now();
      this.drawer.style.touchAction = "none";
      // On touch, don't capture — document listeners are sufficient for dismiss tracking,
      // and capture would redirect events away from Swiper and our _attachTouchZoom handlers.
      if (!this.isTouch()) {
        this.drawer.setPointerCapture?.(e.pointerId);
      }

      document.addEventListener("pointermove", this.onPointerMove, {
        passive: false,
        capture: true,
      });
      document.addEventListener("pointerup", this.onPointerUp, {
        passive: true,
        capture: true,
      });
      document.addEventListener("pointercancel", this.onPointerUp, {
        passive: true,
        capture: true,
      });
    }

    onPointerMove(e) {
      if (!this.drag.active) return;

      if (this.drag.locked === "pan") {
        const main = this.swipersByPanel?.images?.main;
        const zoom = main?.zoom;
        if (!main || !zoom) return;
        e.preventDefault();
        const dx = e.clientX - this.drag.startX;
        const dy = e.clientY - this.drag.startY;
        const scale = zoom.scale ?? zoom.currentScale ?? 1;
        const slide = main.slides?.[main.activeIndex];
        const maxX = ((slide?.offsetWidth || window.innerWidth) * (scale - 1)) / 2;
        const maxY = ((slide?.offsetHeight || window.innerHeight) * (scale - 1)) / 2;
        const newX = Math.max(-maxX, Math.min(maxX, (this.drag.panBaseX ?? 0) + dx));
        const newY = Math.max(-maxY, Math.min(maxY, (this.drag.panBaseY ?? 0) + dy));
        if (typeof zoom.setTransform === "function") {
          zoom.setTransform(newX, newY, scale);
        } else {
          const c = slide?.querySelector(".swiper-zoom-container");
          if (c) c.style.transform = `translate3d(${newX}px, ${newY}px, 0) scale(${scale})`;
          zoom.currentX = newX;
          zoom.currentY = newY;
        }
        // Throttled diagnostic — if pan ever "barely moves" again, this log
        // tells us whether OUR writes are clamping vs Swiper overwriting.
        if (__pidDebugEnabled || window.__pidDebugZoom) {
          const now = performance.now();
          if (!this._lastPanLogAt || now - this._lastPanLogAt > 80) {
            this._lastPanLogAt = now;
            __pidLog("pan move", {
              d: { dx: Math.round(dx), dy: Math.round(dy) },
              new: { x: Math.round(newX), y: Math.round(newY) },
              max: { x: Math.round(maxX), y: Math.round(maxY) },
              scale,
              zoomXY: { x: Math.round(zoom.currentX ?? 0), y: Math.round(zoom.currentY ?? 0) },
              allowSlideNext: main.allowSlideNext,
              allowSlidePrev: main.allowSlidePrev,
            });
          }
        }
        return;
      }

      const dx = e.clientX - this.drag.startX;
      const dy = e.clientY - this.drag.startY;

      this.drag.lastY = e.clientY;
      if (!this.drag.locked) {
        const ax = Math.abs(dx);
        const ay = Math.abs(dy);
        if (ax < 6 && ay < 6) return;
        if (ax > ay) {
          this.drag.locked = "swiper";
          if (__pidDebugEnabled) __pidLog("drawer drag locked -> swiper", { dx: Math.round(dx), dy: Math.round(dy) });
          this.onPointerUp(e);
          return;
        }
        if (dy > 0) {
          this.drag.locked = "dismiss";
          this.drawer.style.touchAction = "none";
          if (__pidDebugEnabled) __pidLog("drawer drag locked -> dismiss (down)", { dx: Math.round(dx), dy: Math.round(dy) });
        } else {
          this.drag.locked = "swipeUp";
          this.drawer.style.touchAction = "none";
          if (__pidDebugEnabled) __pidLog("drawer drag locked -> swipeUp", { dx: Math.round(dx), dy: Math.round(dy) });
        }
      }

      if (this.drag.locked === "dismiss") {
        e.preventDefault();

        const translate = Math.min(dy, window.innerHeight);
        this.drawer.style.transition = "none";
        this.drawer.style.transform = `translateY(${translate}px)`;
        return;
      }
    }

    onPointerUp(e) {
      if (!this.drag.active) return;

      if (this._ignoreNextPointerUp) {
        this.drag.active = false;
        document.removeEventListener("pointermove", this.onPointerMove, {
          capture: true,
        });
        document.removeEventListener("pointerup", this.onPointerUp, {
          capture: true,
        });
        document.removeEventListener("pointercancel", this.onPointerUp, {
          capture: true,
        });
        this.drawer.style.transition = "";
        this.drawer.style.transform = "";
        this.drawer.style.touchAction = "";
        return;
      }

      const lock = this.drag.locked;
      this.drag.active = false;
      document.removeEventListener("pointermove", this.onPointerMove, {
        capture: true,
      });
      document.removeEventListener("pointerup", this.onPointerUp, {
        capture: true,
      });
      document.removeEventListener("pointercancel", this.onPointerUp, {
        capture: true,
      });

      const dy = this.drag.lastY - this.drag.startY;
      const dt = Math.max(1, performance.now() - this.drag.startT);
      const velocity = dy / dt; // px per ms

      this.drawer.style.transition = "";
      this.drawer.style.transform = "";
      this.drawer.style.touchAction = "";

      if (__pidDebugEnabled) {
        __pidLog("drawer pointerup", __pidEventSnapshot(e), {
          lock: lock,
          dy: Math.round(dy),
          dt: Math.round(dt),
          vY: Math.round(velocity * 1000), // px/s
          navType: __pidGetNavType(),
          scroll: { x: window.scrollX, y: window.scrollY },
        });
      }

      if (lock === "dismiss") {
        const shouldClose = dy > 120 || velocity > 0.6;
        if (shouldClose) this.close();
        return;
      }

      if (lock === "swipeUp") {
        // dy is negative when swiping up
        const shouldNext = dy < -80 || velocity < -0.6;
        if (shouldNext) {
          this.getActiveMainSwiper()?.slideNext?.();
        }
        return;
      }

      if (lock === "pan") {
        if (__pidDebugEnabled || window.__pidDebugZoom) {
          const main = this.swipersByPanel?.images?.main;
          const zoom = main?.zoom;
          __pidLog("pan end", {
            scale: zoom?.scale ?? zoom?.currentScale ?? 1,
            zoomXY: { x: Math.round(zoom?.currentX ?? 0), y: Math.round(zoom?.currentY ?? 0) },
            activeIndex: main?.activeIndex,
            allowSlideNext: main?.allowSlideNext,
            allowSlidePrev: main?.allowSlidePrev,
          });
        }
        // If the user has ended back at <=1x somehow (e.g. shift+wheel mid-pan
        // or zoomChange flipped state), restore slide nav so the carousel is
        // swipeable again.
        if (!this.isZoomed()) this._setSwipeNavAllowed(true);
        return;
      }
    }

    // §TOUCH_ZOOM
    _attachTouchZoom(mediaListEl, swiper) {
      if (swiper.__pidTouchZoom) return;
      swiper.__pidTouchZoom = true;

      const getContainer = () => {
        const slide = swiper.slides?.[swiper.activeIndex];
        return slide?.querySelector(".swiper-zoom-container") || null;
      };

      // Fake Swiper zoom interface — keeps zoomToScale, isZoomed, zoomChange wiring, etc. working unchanged.
      const fake = {
        scale: 1,
        currentScale: 1,
        currentX: 0,
        currentY: 0,
        isScaling: false,
        setTransition: () => { },
        setTransform: (x, y, scale) => {
          const c = getContainer();
          if (!c) return;
          const s = Math.max(1, Math.min(3, scale));
          fake.scale = s;
          fake.currentScale = s;
          fake.currentX = x;
          fake.currentY = y;
          c.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
          swiper.allowTouchMove = s <= 1.01;
        },
        out: () => {
          fake.setTransform(0, 0, 1);
          this.drawer.classList.remove("pid-zoomed");
          this.updateZoomReadoutFrom(1);
          this._zoomTarget = 1;
        },
        in: () => {
          fake.setTransform(0, 0, 2);
          this.drawer.classList.add("pid-zoomed");
          this.updateZoomReadoutFrom(2);
          this._zoomTarget = 2;
        },
      };
      swiper.zoom = fake;

      const ptrs = new Map();
      let pinchBase = 1;
      let pinchDist0 = 0;
      let lastTapTime = 0;
      let lastTapX = 0;
      let lastTapY = 0;
      let panActive = false;
      let panStartX = 0;
      let panStartY = 0;
      let panBaseX = 0;
      let panBaseY = 0;

      const ptrDist = () => {
        const [a, b] = [...ptrs.values()];
        return Math.hypot(b.x - a.x, b.y - a.y);
      };

      mediaListEl.addEventListener("pointerdown", (e) => {
        if (!this.isOpen) return;
        if (e.target.closest(".main-media-zoom-indicator-wrapper")) return;
        ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (ptrs.size === 2) {
          fake.isScaling = true;
          panActive = false;
          pinchDist0 = Math.max(1, ptrDist());
          pinchBase = fake.scale;
          swiper.allowTouchMove = false;
          return;
        }

        if (ptrs.size === 1) {
          const now = Date.now();
          const dx = e.clientX - lastTapX;
          const dy = e.clientY - lastTapY;
          if (now - lastTapTime < 280 && Math.abs(dx) < 50 && Math.abs(dy) < 50) {
            lastTapTime = 0;
            this._suppressNextMediaClick = true;
            if (fake.scale > 1.01) {
              fake.out();
            } else {
              fake.in();
            }
            return;
          }
          lastTapTime = now;
          lastTapX = e.clientX;
          lastTapY = e.clientY;

          if (fake.scale > 1.01) {
            panActive = true;
            panStartX = e.clientX;
            panStartY = e.clientY;
            panBaseX = fake.currentX;
            panBaseY = fake.currentY;
            swiper.allowTouchMove = false;
          }
        }
      }, { passive: true });

      mediaListEl.addEventListener("pointermove", (e) => {
        if (!ptrs.has(e.pointerId)) return;
        ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (ptrs.size >= 2 && fake.isScaling) {
          const s = Math.max(1, Math.min(3, pinchBase * (ptrDist() / pinchDist0)));
          fake.setTransform(fake.currentX, fake.currentY, s);
          this.drawer.classList.toggle("pid-zoomed", s > 1.01);
          this.updateZoomReadoutFrom(s);
          this._zoomTarget = s;
          return;
        }

        if (ptrs.size === 1 && panActive && fake.scale > 1.01) {
          const c = getContainer();
          if (!c) return;
          const dx = e.clientX - panStartX;
          const dy = e.clientY - panStartY;
          const slide = swiper.slides?.[swiper.activeIndex];
          const maxX = ((slide?.offsetWidth || window.innerWidth) * (fake.scale - 1)) / 2;
          const maxY = ((slide?.offsetHeight || window.innerHeight) * (fake.scale - 1)) / 2;
          fake.setTransform(
            Math.max(-maxX, Math.min(maxX, panBaseX + dx)),
            Math.max(-maxY, Math.min(maxY, panBaseY + dy)),
            fake.scale,
          );
        }
      }, { passive: true });

      const onPointerEnd = (e) => {
        ptrs.delete(e.pointerId);
        if (ptrs.size < 2 && fake.isScaling) {
          fake.isScaling = false;
          if (fake.scale < 1.15) fake.out();
          else swiper.allowTouchMove = false;
        }
        if (ptrs.size === 0) {
          panActive = false;
          if (fake.scale <= 1.01) swiper.allowTouchMove = true;
        }
      };

      mediaListEl.addEventListener("pointerup", onPointerEnd, { passive: true });
      mediaListEl.addEventListener("pointercancel", onPointerEnd, { passive: true });
    }
  }

  // §OPEN_HANDLER
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-open-product-image-drawer]");
    if (!btn) return;

    const openPanel = btn.dataset.pidOpenPanel;
    const openIndex = btn.dataset.pidOpenIndex;
    const mediaId = btn.dataset.mediaId;
    const rootId = btn.dataset.pidRoot;
    const root = rootId
      ? document.querySelector(`[data-product-image-drawer-root="${rootId}"]`)
      : document.querySelector("[data-product-image-drawer-root]");
    const pid = root?.__pid;
    if (!pid) return;

    let index = 0;
    let panel = "images";

    if (openPanel === "videos" && Number.isFinite(parseInt(openIndex, 10))) {
      panel = "videos";
      index = parseInt(openIndex, 10);
    } else if (mediaId && root) {
      const imgSlide = root.querySelector(
        `[data-pid-panel="images"] [data-pid-media-list] [data-media-id="${mediaId}"]`,
      );
      const vidSlide = root.querySelector(
        `[data-pid-panel="videos"] [data-pid-media-list] [data-media-id="${mediaId}"]`,
      );

      const targetSlide = vidSlide || imgSlide;
      if (vidSlide) panel = "videos";

      const filteredIndex = targetSlide
        ? parseInt(targetSlide.getAttribute("data-filtered-index"), 10)
        : NaN;

      if (Number.isFinite(filteredIndex)) index = filteredIndex;
    }

    pid.open({ index, panel });
  });

  // §INIT
  document.addEventListener("DOMContentLoaded", () => {
    document
      .querySelectorAll("[data-product-image-drawer-root]")
      .forEach((root) => {
        root.__pid = new ProductImageDrawer(root);
      });

    // global hook
    window.openProductImageDrawer = (index = 0, rootId) => {
      const root = rootId
        ? document.querySelector(`[data-product-image-drawer-root="${rootId}"]`)
        : document.querySelector("[data-product-image-drawer-root]");
      root?.__pid?.open({ index });
    };
  });
}
